import React, { useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import {
  FDBox,
  FDContentContainer,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack, useFDBodyColor,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { SecondaryActionButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/SecondaryActionButtonRel';
import { useLayoutContext } from 'Context/LayoutContext';
import { makeLinkOpenInFocusMode } from 'Helpers/Navigation';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import { RoadmapExplorerPathsProvider } from 'Components/UI/guidedHouseholdReview/pathsContext';
import { GuidedHouseholdReviewLeftNav } from 'Components/UI/guidedHouseholdReview/leftNav';
import { WizardBackButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/WizardBackButtonRel';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { GuidedHouseholdReviewLayoutHeader } from './layoutHeader';

const useStyles = buildUseStyles('GuidedHouseholdReviewLayoutWrapper', () => ({
  flexibleChild: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '& > *': {
      display: 'flex !important', // Needed to override FDContentContainer's display:block
      flexDirection: 'column',
      flex: 1,
    },
    minWidth: 0,
  },
  invisible: {
    visibility: 'hidden',
  },
}));

const ARBITRARY_WIDTH_TO_BE_REMOVED_LATER = 500;

const GuidedHouseholdReviewLayoutWrapper = ({
  children,
  condensed,
  editLink,
  customButtons,
  fullWidth,
  hideTitle,
  hideSave,
  showSidebar,
  SidebarComponent,
  color,
  fullHeight,
}) => {
  const { resource } = useMetaLayoutContext();
  const classes = useStyles();
  const [, setSidebarWidth] = useState(ARBITRARY_WIDTH_TO_BE_REMOVED_LATER);
  const [savingSidebar, setSavingSidebar] = useState(false);
  useFDBodyColor(color);
  const { displayErrorMessage } = useSnackbarContext();
  const { setPageContentCollection } = usePageContentContext();
  const [submitting, setSubmitting] = useState(false);
  const submitPage = async () => {
    try {
      setSubmitting(true);
      setPageContentCollection(await resource.submit());
    } catch (err) {
      displayErrorMessage(err, err?.errors || 'We encountered an error, please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const resourceType = resource && typeof resource === 'object' && 'type' in resource ? resource.type() : '';
  const { logStandardizedEvent, rootNodeProps } = useEventTrackingLogEvent({
    nodeName: resourceType,
  });

  const onEdit = () => logStandardizedEvent(categories.pm1OrPM2, eventTypes.edit, 'editing', { eventProperties: { resourceType } });

  const { isFocusedLayout } = useLayoutContext();
  const overwriteWithFocusMode = isFocusedLayout
    ? makeLinkOpenInFocusMode
    : (input) => input; // Since we're not in focused layout, we don't have any overwriting to do

  const header = <GuidedHouseholdReviewLayoutHeader condensed={condensed} hideTitle={hideTitle} logStandardizedEvent={logStandardizedEvent} resourceType={resourceType} />;

  const content = (
    <FDStack
      direction="column"
      spacing={3}
      justifyContent={condensed ? 'flex-start' : 'center'}
      flex={1}
      {...dataAttrs('guided-household-review-main-content-container')}
    >
      {(condensed || hideTitle) ? null : <MetaControlField attr="title" />}
      <FDStack direction="column" spacing={2} justifyContent={condensed ? 'normal' : 'center'} paddingX={4} flex={fullHeight ? 1 : undefined}>
        {children}
      </FDStack>
      {customButtons ? null : (
        <FDStack direction="row" justifyContent="center" alignItems="center" spacing={2}>
          <WizardBackButtonRel resource={resource} size="lg" />
          {resource.withRelationship(
            fr.relationships.edit.name,
            (rel) => <SecondaryActionButtonRel relationship={rel} forceFocusMode={isFocusedLayout} size="lg" afterSuccessfulOnClick={onEdit} />,
            () => (editLink ? (
              <FDSecondaryActionButton
                href={overwriteWithFocusMode(editLink)}
                openInNewTab
                label={resource.attr('editLabel')}
                size="lg"
                onClick={onEdit}
              />
            ) : null),
          )}
          <FDPrimaryActionButton
            {...dataAttrs('submit-btn')}
            loading={submitting}
            onClick={submitPage}
            label={resource.attr('primaryActionLabel') || 'Submit'}
            icon={'arrowRight'}
            iconPosition={'end'}
            disabled={!resource.meta().canSubmit()}
            size="lg"
          />
        </FDStack>
      )}
    </FDStack>
  );

  return (
    <RoadmapExplorerPathsProvider resource={resource}>
      <FDStack padding={2} flex={1} height={isFocusedLayout ? '100vh' : undefined} key={`roadmap-explorer-paths-${resource.id()}`}>
        <div className={classes.flexibleChild} {...rootNodeProps}>
          <FDBox padding={2} flex={1}>
            {header}
            <div className={classes.flexibleChild}>
              {fullWidth ? content : <FDContentContainer maxWidth="md">{content}</FDContentContainer>}
            </div>
            {/*
            To make the content vertically centered while having a header, we need a footer at the bottom that takes up the exact same space as the header.
            so render the header again but this time make it invisible
          */}
            <div className={classes.invisible}>
              {header}
            </div>
          </FDBox>
        </div>
        {showSidebar && <SidebarComponent onPickNewSize={setSidebarWidth} setSaving={setSavingSidebar} {...dataAttrs('guided-household-review-sidebar')} />}
      </FDStack>
      {!hideSave && (submitting || savingSidebar) ? <LoadingOverlay /> : null}
    </RoadmapExplorerPathsProvider>
  );
};

GuidedHouseholdReviewLayoutWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  editLink: PropTypes.string,
  customButtons: PropTypes.bool,
  condensed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  hideTitle: PropTypes.bool,
  showSidebar: PropTypes.bool,
  hideSave: PropTypes.bool,
  color: PropTypes.oneOf(['emphasized', 'subtle']),
};

export const withGuidedHouseholdReviewLayout = (
  Component,
  {
    condensed = false,
    fullWidth = false,
    editLink = undefined,
    customButtons = false,
    hideTitle = false,
    showSidebar = false,
    SidebarComponent = undefined,
    renderLeftNav = true,
    fullHeight = false,
    color = 'subtle',
    hideSave = false,
  } = {},
) => {
  return (props) => (
    <GuidedHouseholdReviewLayoutWrapper
      editLink={editLink}
      customButtons={customButtons}
      condensed={condensed}
      fullWidth={fullWidth}
      hideTitle={hideTitle}
      showSidebar={showSidebar}
      SidebarComponent={SidebarComponent}
      color={color}
      fullHeight={fullHeight}
      hideSave={hideSave}
    >
      <Component {...props} />
      {renderLeftNav ? <GuidedHouseholdReviewLeftNav size="lg" /> : null}
    </GuidedHouseholdReviewLayoutWrapper>
  );
};
